import {
  assertEvent,
  assign,
  fromPromise,
  sendTo,
  setup,
  type SnapshotFrom,
} from 'xstate';
import type {IEvent} from '../../types/IEvent';
import {bookingMachine} from '../../machines/bookingMachine';
import {identificationMachine} from '../../machines/identificationMachine';
import {rsvpMachine} from '../../machines/rsvpMachine';
import {
  cancelEvent,
  getEvent,
  getEventTemplate,
  getReasons,
  getSession,
  rescheduleRequest,
} from '../../services/schedularService';
import type {IParticipantType} from '../../types/IParticipantType';
import {getShortSha256Hash} from '../../utils/crypto';
import {isTimezoneSupported} from '../../utils/booking';

import type {
  IRootMachineContext,
  IRootMachineEvents,
  IRootMachineInput,
} from './rootMachine.types';
import {getRsvpStatus} from '../../utils/common';
import {isBrowserSupported} from '../../utils/browser';


export const rootMachine = setup({
  types: {
    context: {} as IRootMachineContext,
    events: {} as IRootMachineEvents,
    input: {} as IRootMachineInput,
  },
  actors: {
    getEvent: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getEvent(input.eventId),
    ),
    getSession: fromPromise(async ({input}: {input: {sessionId: string}}) =>
      getSession(input.sessionId),
    ),
    getTemplateBySuffix: fromPromise(
      async ({input}: {input: {agentId: string; suffix: string}}) =>
        getEventTemplate(input.agentId, input.suffix),
    ),
    getReasons: fromPromise(async () => getReasons()),
    getOwner: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; attendee?: string};
      }): Promise<IParticipantType | undefined> => {
        const guestHash = await getShortSha256Hash(`guest_${input.eventId}`);
        if (guestHash === input.attendee) {
          return 'CANDIDATE';
        }
        const hostHash = await getShortSha256Hash(`host_${input.eventId}`);
        if (hostHash === input.attendee) {
          return 'HOST';
        }
        const interviewerHash = await getShortSha256Hash(
          `interviewer_${input.eventId}`,
        );
        if (interviewerHash === input.attendee) {
          return 'INTERVIEWER';
        }
        return undefined;
      },
    ),
    cancelEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          reason: string;
          viewerTimezone: string;
          attendee: string;
          aid: string;
        };
      }) =>
        cancelEvent(input.eventId, {
          viewer_timezone: input.viewerTimezone,
          attendee: input.attendee,
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    sendRescheduleRequest: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; reason: string; aid: string};
      }) =>
        rescheduleRequest(input.eventId, {
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    generateAttendee: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getShortSha256Hash(`guest_${input.eventId}`),
    ),
    rsvpMachine,
    bookingMachine,
    identificationMachine,
  },
  guards: {
    isBrowserNotSupported: () => !isBrowserSupported(),
    isRescheduleLimitReached: ({context}) => {
      return (context.eventDetails as IEvent)?.reschedule_attempts_left === 0;
    },
    isEditBooking: ({context}) => {
      if (!context.currentRoute) {
        return false;
      }
      return ['cancel', 'reschedule', 'rsvp'].includes(context.currentRoute);
    },
    isSession: ({context}) => !!context.sessionId,
    isCancellationAndNotSequential: ({context}) => {
      const sequentialEvent =
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id);
      return context.currentRoute === 'cancel' && !sequentialEvent;
    },
    isRescheduleAndNotSequential: ({context}) => {
      const sequentialEvent =
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id);
      return context.currentRoute === 'reschedule' && !sequentialEvent;
    },
    isRsvp: ({context}) => context.currentRoute === 'rsvp',
    isCancellationNoticePass: ({context}) =>
      !!(context.eventDetails as IEvent)?.did_cancellation_notice_pass,
    isAttendeeAvailable: ({context}) => !!context.attendee,
    isHostOrInterviewer: ({context}) =>
      context.owner === 'HOST' || context.owner === 'INTERVIEWER',
    isCandidate: ({context}) => context.owner === 'CANDIDATE',
    isSessionExpired: ({context}) => !!context?.session?.is_expired,
    isTimezoneSupported: ({context}) =>
      context.timezone ? isTimezoneSupported(context.timezone) : false,
    isBookingSuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'BookingSuccess';
    },
    isSequentialEvent: ({context}) => {
      // Check if eventDetails is an array  for booking sequence OR event has group_id for any edit booking
      return (
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id)
      );
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCcD2qAuBiANgQwDsoBXPGAOmIAcI8MwBtABgF1FQrVYBLDb1AuxAAPRAEYAHADYAzOSYBOAExMALBLEB2KWMkKANCACeiALSbNE+duVKlszQFYmEgL6vDaTOVgY8ybGY2JBBOHj4BIVEEUzEZdXIZBRlHCxkpKQks1UMTGMcZOXUlVQySnSltd090DB8-AKwGMWCOLl5+QRDo0wlHJXJHZyYZCUVpKQUJXLMCoolyqXKxSqlqkC86339ApVbQ9oiu0GiV8lUlZQ0rqbixGfylKwvNC4VNJk-Stw8N2vI+ABbMAALwEYAAMqg8BBIE1WEIwh1It0zNpnk5rjIVBIFP0HqYUkx5GIFKoZDYxKpkqp1ptyMgwHhYAJYFCYdwiFgIODyJyAG6oADWYAZ-0ZzNZ7IgnKgCAFqAAxnROkEgojDp0omYJJoFPIRkoPrqMtJHATlIlPo4FApMnYjVM6eKmSyCGzoTKuTyCKKFSKxd4JW6PRyiPKCILlUc1S0NeEtaiYlJVOQlPE9Y5SUwsxoZBaBjJrbb7XY9T8akHXVLPbLubz-aL6cGa2G5Qro6rWAw9vHkccRIhdZpyLIniUqallJoCWImCPM2ox6pNHFKs6q5L3dK62BkGhkOQqPgMAAzVDIQGBuot7e18MdlUCNUIkJIo7amKqZyJUnqYdDHq5IEkoWiju86gfCUKhlhuN7Vu6ACi+4XlgjIYMgRjqm+mooicswWOQfRPEWozpJ8UggVk5BOGRq4aAU85wQC3DAmCvoAArcIqIrIFgQKgrysBgBg2FtAmeGDjEFKOKOpQFFInzphc9gEiuxJTgsTwSNiJHMWA-JgAQGA7t6DaRsKTb-AZRkmfe7YWZ2z7dq+4n9p+vQjGmOifKS2irpcDzfvqryktijiZJkEi0r89I2cZplQFge4HkeJ7npe17kPFdlthGUZPgQL77O+ib4V+qhiKOnz0VSqj1RFDxiBF5BxPOajzoUKj9MxwmwDwAiJfWvp8hZAb0n1A0EIl+VKoVxV9h+SbOFVWgjIo4xMHo5rGIgFJWHYahzq8ihPL1cBTUNKUXmldAZVeE0XZ0M2PjGLklbhA6nEwlyJF1GQ2mIRo7Xk3WJOmeIWJcVJ4vphnGQAKmAgLHnQkL2cNfpjVZ3g5UjKMnujeWvV2LBiQcElfeIkypgpc4-ZVK6pA8OkDDmyQ2JoSipFIjhw7Z+Oo-QV0oYeQv3VlePI0LRNeg5BVvWTrkU+5SagS1MhUtz9UpKoiiUbtCC86mtqgQ6QGSHzsX-LC9CXpyYAAPIAO6+nxPpY4K402yJe6Ag7Ltu7NTlFe9i1lVJnlSPIdrvJ8W0G3kXMDNoOhUrz0hzhWfy4-DuVy8hB5oSJmHk6Vkk9KkI7EbiRofM1jjTIbpjEvY6QpoU6Rc68mvnf1z2enCZefZ+-6jlBygpnOjoPJUVj0bzQPaDrMh95dg8QE0cY4ZTn5UvEVpA43mQ5hSs86QaNI6HiK5aGvnSF6h6Gl8r5dUwgOmyRYihDCo6TKPcQ2WtWrc3ZtSSQoEFDMQAEboCFLKAAYjgVAztMajS9qKWBwpEHIOdsPXeSYVgJFtEWak-9KpkiakDKq6lbi4lNK8GBcCcEoLQY2cgWD4FECQSg5oH0CHlVAimOSGQao6VtI4HIQC4gjkbribQK0ChiCYdg7huDcCEBIGQUU1BaD0HwarQRa1RzSHiAUO0n98yG2igMP8K1yR6yWJofmiNpaE0fnxZ+WFX4jzViMOQOZrRkOUOSJQDxJFVRxIUTIc50g9Wtt4FBbtpRDx8QIyOmciIlHjqsHQCwHjvFktPU0YxyQ6VXgkuoSS9wpM3nw8OFc9p63ILaW0IxeZRT-AUsY5xyw2izJkXSzFqnIFqU0XsO9DFSQUPOUcKxFJMB0OoW0gC8hTGJCud4GZyRc1JMM12NSN5NBkPwqZpwKRyGxOoSQshCnpAKU4VqP1JF601kaS4KiuFQAAMrEEVIqC68JTlLXKloUkrUubRQpNSG+Tc8hrVprIFM3dLiSIqZWOonDZS-P+YC+pkyQVSW6iFbmy9rjSCkfCgK5weaFBqj-KBlTyAwDdmjAAghgegBBYRgDYdjLKLK9zss5UZHlwd5phwJRHU4qdEgfGcJUOuKwwlALxFVKch0nBczUMxZUBAAU4BPJ0HhztRq8CBQ09+sRqRf2BpIMpuJvxNSLFVRZagtJJAyCkXVhADVGoECas1gRt5uUJT0ahqZ4iVFeN8eczVnVbXkIpYoEMvVWwxeQPVfrCqBs5OanswLpU6h+okZwCxnnaqmAm11yaPV2lkOmnOdQs1gENTm3BDIEIIIvFee6xctzdsyrAYg0D-aiTSWcswuhdSDDLO8CwjdJxNTkfIHSlgkia2NEoH1+rW3+oIIG28g6rzDtHbwPgZkRrsPpC2ttRxD1dp7T4EdY6L3yzmorAxYap2SGJMoRQHx4ilD1pS8QeJo4Uk+BIyo3xnFMtvfuh9A6n2ntfbuUWt0zxPpvb6vd7aUGduQ0Ol957ZTis-RO79MQ9TRyBooSqusVhbSatfeQqK4jcwWHidFTbM24bvcajtR6n3XU8Y+4jZ7x2FsaTEZIRRtLaXUBFZmQChjqqSFOQoCgwprCZYyWAioAAWkBiA4DAIGnArFeAADlMAACUmRGdSdJ9+dgv6OP-Rp7WM4gEqTTNzAoTwYmjGzs2OATmICmfMx2yzY7bMYAc3gCLW8XOfiGNHJwU9AuTFsM620aZlkZA7mOODGb9MRaixZqzGB4uJeSwWy1n4xhVVtFkS4U8E4qvhX5tzgXpB5iyMxcrxnItmaQ26Y95A+3CYk2Or9Rb8ghXiCsadilUjkhBuIMYhZNYUgsMkLM6QhvhZG5VoT4mT0kc5XWD26DLJZWGyZsb52iOXck2+8jpN5syckbY-yK25iaCsfCyKIDv6gQsOoXuemTtPeiwRmbb20NclE5hiWYWDOneewji7z73tkZJs5JWqXloXAhZUAHhQgcsekGDqCWhXg6WUTDzHcPxsBpE6LftE2UNXe++-Bj8hKqkm043TVidxBA9TEvenkOmfHdZ6N+HpqsVqNYbd9hquoAmv53vCk+oswlBtYswpqzxBZiqtCkYIT9apAVxV7HKvmFq9QRr-lWudchpVlRsKlv5yTCBuOdQEuEDNRKC0i45Itp6lJKBe3WPlccOd9r9R+AiCkAoLotGuvCHusGIFywhST5wvN9p84SxAKZapEDePbOO15sCJRhbpghg0UUCsFZ-WuabdD5cOQ6YjeLL1lkO3LOHeJ4bylxratfpA6GCkRulVcxdfEJcfUB2tUwX6DpT5soADC-Hj1YC1wf3dOBJuod4Dn8qSRiTV5tDRo0msDCGwh1YXQNJbSWCB6V3jJ-+O-L46Xqez3b0j-5n6AHI7vohwLRSoya9AzKJAphKI2qdw+agwwSDCQxPB0YUi6YZrgEGqQGkYo4YbizYb-CEGtrEHXYPiOQSrE7T7lQIHnB6xTiWDfgjDASv6KD6iKRZBQoLraa75ECn4GoeLH7J5iGtoX585N7wHfhVQ6RTxZC2C6Ar4IAqDkhESlD2DRRrZA6hb-B0Bco8oADiRkQq9Am8j2SuzKqA1+UkVIPSQOf4Fwn8QMIekgKQLSMyE82g0MzEJhoqYAYAFhrK1hWACGhU9hjhpwK4I4W0msfQ8qGg0UDwh0rqkM9UT+k4umvwBAqAsI8AIQmwTBkcHexIowf8zUbWjcPepgSwf6Cwih9gzggGvUDQGA5R4aNo0cdoP0OYzUP0GYBIjE48rwdgQMm6W0zExA7o1AnAAQkAAAQmgM7MJMgD0VOvEHIFoH0FmP5OMOgWYHrJEjMkMDMskHPDqkygJOxLLJANsTEBoKtGoIYZYM4JVCcRVC1pkCpDcgHkYZuCGIlM8b0OCupP-K8P8ekc3NzKtNSPOB8KUEWB0sdluLAB4uCRcJcuRDYC4FGqUFRPPAUGupIM1EWL-vSPceCFxDxHuM8UDEmioDMvatpi4GpCuNVNiGUJUEkNpjFBmjlGCXAVai0awXOM1KnLpD8ZSURKkEbriboE4I2o9P3INPZM8dchCuUDmJIlcNoE1BfJVGnJSdiEkEKbxlLATGjKKaGgth3q6tiISWwZbC-nkGUDoRtDzL5PYMxLbH7AHAclsWKR5NcHMvYO4Q-iMGbggDaHflBkploMpFaXFHnIlNiWGUmKYBtucGMHPlmFcauASK3Eih3NEt3KUBkPfJqTCE8dmeVDCq1IpK4WnBYCHocW3qzBukzkxEypNA-KLEyYaCYtDBFDCSUCXqHmTkfGAtpgsPaiISnigkyYqq1EWXrN-FzEsAUtSKWvYBSWLg1C4hgILO4sOY2USloAEgnKYuAhkOEqUDROmGkIuuSLaPsskhvOCZYPPN+P1ukF3AUCzJIq1FMFzHiDzCmGmf8MJAAI7EC2TcB4A4CIR5yIQyg1aYBsqGooINkOkyZZAtafFbRJHWhyn0ZEQ-wUKkKsy1kECITCBUDcCMgQDakzr2B9I7Kyk96fF-RAwNRU7vCMoEHJ44oAr9RMlA5yD9BvB4iEkMwsaJo1TawbQKKiW8aCrIDCqmGhHPEqCVCtTeH7zD5PBNTXGsEZAf66w1kDlgCIXIWoUrHiV-KSUlGEXvxrQHTkjYjaCdT0RNR6j6gHEpDYgUrHTMSFF8CnhGASUXQGUpDOlPCKEnQKrLp2DgXpgaBlKfC4g7rZr3q4LglEKFjlqaxyKcY94f7v4ODC5FipBDAFV4ZFUEYN4lU2iRrlW1ELD1FNSXBWAaD6mtkriwXeDRGtWmoTWdAcSoCWaKh5CeUeSThWARQ8XP7JDTn7wDAOCrjJB6HYjNUCYBovY86XglXpiyT9COgWCYhLq+bYg0puq3I-QFlHWIanUc6ZT3QlUrZESlLRQNQDVeFFktLODb7B46AWDvX4amqI545QG-UZBIFXJPCTxrYsb1SDCrgXCNwuBnTwb8YfU46vbZSXlLU5mVCyTKCjCB5h7lDLpZiDAaZ6haY6Yw2TV8iwjGTcCnjcSw3gn9JpjlK-opCvCSL9V9Cji3JxBFiCkUi15K4mrglTmjhTmgIuraaPnSJkhESuHRRLyVTpiK1nYEaxY2b2aOYjYGWgSJALncxo3EQgVAIpD6guqjA-RaxTkm2O6EZnWAgq2fBq3FDOCa3zLLpTBq1OBr5TCi4+2J7w0-VXm9E7Xq2h1bRa1eG9X56s17ZJC4jAnwSK6m1w246X60FQBrkbJcWU4XJqDGmzJkjJAZ19AvDx3s4Hqc4HjPFenb6vVU63IaFaDNKolQRkKzFj4J6Boe7FXJ2IBLADCOL2DgYTk-TLqSDnCvmx02Lljt317c0xX82TUjnaFuYjDbayA5hNR6whV6FXHB4LD4G8a2El1BolVA7GXKQf4QLbaS2FiuGLJOCFJOiT11447F1mYQjVZ1bW1z1fiyQ-QKVGidRR5bUe00QpjZBaHSCaXNiwD8hUDK1wMyLRxjDtH02BRAI-w0paB9H1RxDLnSHn49ojk5j55JCQ6rjf4emICZExzrYvCaylCMMAFXZvrgmLy-hkrvK2jqAZEM5YERQZ0ph4jRRHXxVSVwOxCSAjhkiZx3CNxkhxmQIhXfjaApBr4uAiNn5ZkU3MF0aDC6jTFcbSkpAZFqByD6F6GlA5hA5P30gOaOW+AOYQNgAaMeXe4LaQxpjTzf7NS+UWU+G8z0RkjL2jC4PGEircqhHhFWEEWRMybpxyAZAKWLIwUpEWVczUXJolBtIMPuCuBAA */
  context: ({input}) => ({
    id: input.id,
    currentRoute: input.currentRoute,
    attendee: input.attendee,
    aid: input.aid,
    timezone: input.timezone,
    sessionId: input.sessionId,
    language: input.language,
    countyCode: input.countyCode,
  }),
  id: 'root',
  initial: 'start',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => event.language,
      }),
    },
  },
  states: {
    start: {
      always: [
        {
          target: 'unsupportedBrowser',
          guard: {
            type: 'isBrowserNotSupported',
          },
        },
        {
          target: 'timezoneLoaded',
          guard: {
            type: 'isTimezoneSupported',
          },
        },
        {
          target: 'timezonePicker',
        },
      ],
    },

    unsupportedBrowser: {
      type: 'final',
    },

    timezoneLoaded: {
      always: [
        {
          target: 'reasonsLoading',
        },
      ],
    },

    reasonsLoading: {
      invoke: {
        id: 'rootNew.reasonsLoading:invocation[0]',
        input: () => ({}),
        onDone: [
          {
            target: 'eventLoading',
            guard: {
              type: 'isEditBooking',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'sessionLoading',
            guard: {
              type: 'isSession',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'eventTemplateLoading',
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
        ],
        onError: {
          target: 'reasonsError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getReasons',
      },
    },

    reasonsError: {
      on: {
        retry: {
          target: 'reasonsLoading',
        },
      },
    },

    timezonePicker: {
      on: {
        'timezone.set': {
          target: 'timezoneLoaded',
          actions: assign({
            timezone: ({event}) => event.timezone,
          }),
        },
      },
    },

    eventLoading: {
      invoke: {
        id: 'rootNew.eventLoading:invocation[0]',
        input: ({context: {id}}) => ({eventId: id}),
        onDone: {
          target: 'determineOwner',
          actions: assign({
            eventDetails: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventLoadingError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getEvent',
      },
    },

    sessionLoading: {
      invoke: {
        input: ({context}) => {
          if (context.sessionId) {
            return {
              sessionId: context.sessionId,
            };
          }
          throw new Error('sessionId not found');
        },
        onDone: {
          target: 'sessionLoaded',
          actions: assign({
            session: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'sessionError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getSession',
      },
    },

    eventTemplateLoading: {
      invoke: {
        input: ({context: {currentRoute, id}}) => ({
          agentId: currentRoute,
          suffix: id,
        }),
        onDone: {
          target: 'bookingFlow',
          actions: assign({
            eventTemplate: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventTemplateError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getTemplateBySuffix',
      },
    },

    determineOwner: {
      invoke: {
        id: 'rootNew.determineOwner:invocation[0]',
        input: ({context}) => {
          if ((context.eventDetails as IEvent)?.id) {
            return {
              eventId: (context.eventDetails as IEvent)?.id,
              attendee: context?.attendee,
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'ownerLoaded',
          actions: assign({
            owner: ({event}) => event.output,
          }),
        },
        src: 'getOwner',
      },
    },

    eventLoadingError: {
      on: {
        retry: {
          target: 'eventLoading',
        },
      },
    },

    sessionLoaded: {
      always: [
        {
          target: 'sessionExpired',
          guard: {
            type: 'isSessionExpired',
          },
        },
        {
          target: 'eventTemplateLoading',
        },
      ],
      entry: assign({
        language: ({context}) =>
          context.session?.guest_language || context.language,
      }),
    },

    sessionError: {
      on: {
        retry: {
          target: 'sessionLoading',
        },
      },
    },

    bookingFlow: {
      on: {
        'language.update': {
          actions: [
            assign({
              language: ({event}) => event.language,
            }),
            sendTo(
              ({system}) => system.get('bookingMachine')!,
              ({event}) => ({
                type: 'language.update',
                language: event.language,
              }),
            ),
          ],
        },
      },
      invoke: {
        id: 'bookingFlow',
        input: ({context: {timezone, eventTemplate, language, session}}) => {
          if (timezone) {
            return {
              timezone,
              eventTemplate,
              language,
              session,
            };
          }
          throw new Error('timezone not found');
        },
        src: 'bookingMachine',
        systemId: 'bookingMachine',
        onDone: [
          {
            target: 'bookingSuccess',
            actions: assign({
              eventDetails: ({event}) => event.output.eventDetails,
            }),
            guard: {
              type: 'isBookingSuccess',
            },
          },
          {
            target: 'notifySuccess',
          },
        ],
      },
    },

    eventTemplateError: {
      on: {
        retry: {
          target: 'eventTemplateLoading',
        },
      },
    },

    ownerLoaded: {
      always: [
        {
          target: 'cancellationFlow',
          guard: {
            type: 'isCancellationAndNotSequential',
          },
        },
        {
          target: 'rescheduleFlow',
          guard: {
            type: 'isRescheduleAndNotSequential',
          },
        },
        {
          target: 'rsvpFlow',
          guard: {
            type: 'isRsvp',
          },
        },
        {
          guard: {
            type: 'isSequentialEvent',
          },
          target: 'sequentialEventEditNotAllowed',
        },
      ],
      entry: assign({
        language: ({context}) => {
          const eventDetails = context.eventDetails as IEvent;
          const users =
            context.owner === 'HOST' || context.owner === 'INTERVIEWER'
              ? eventDetails?.interviewers
              : eventDetails?.candidates;

          const currentUser = users?.find((user) => user.id === context.aid);
          return currentUser?.language || context.language;
        },
      }),
    },

    sequentialEventEditNotAllowed: {
      type: 'final',
    },

    sessionExpired: {
      type: 'final',
    },

    bookingSuccess: {
      always: [
        {
          target: 'sequentialBookingSuccess',
          guard: {
            type: 'isSequentialEvent',
          },
        },
        {
          target: 'generateAttendee',
        },
      ],
    },

    generateAttendee: {
      invoke: {
        input: ({context: {eventDetails}}) => ({
          eventId: (eventDetails as IEvent)?.id || '',
        }),
        src: 'generateAttendee',
        onDone: {
          target: 'attendeeGenerated',
          actions: assign({
            attendee: ({event}) => event.output,
            owner: 'CANDIDATE',
          }),
        },
      },
    },

    sequentialBookingSuccess: {
      type: 'final',
    },

    notifySuccess: {
      type: 'final',
    },

    cancellationFlow: {
      initial: 'init',
      states: {
        init: {
          always: [
            {
              target: 'cancellationPolicy',
              guard: {
                type: 'isCancellationNoticePass',
              },
            },
            {
              target: 'reasonForm',
              guard: {
                type: 'isAttendeeAvailable',
              },
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        cancellationPolicy: {
          type: 'final',
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
            submitting: {
              entry: assign({
                reason: ({event}) => {
                  assertEvent(event, 'reasonForm.submit');
                  return event.reason;
                },
              }),
              invoke: {
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  const eventDetails = context.eventDetails as IEvent;
                  if (eventDetails?.id) {
                    return {
                      eventId: eventDetails?.id,
                      reason: event.reason,
                      viewerTimezone:
                        eventDetails?.primary_guest_timezone || '',
                      attendee: context.attendee || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId not found');
                },
                onDone: {
                  target: '#root.cancelSuccess',
                },
                onError: {
                  target: 'error',
                  actions: assign({
                    error: ({event}) => event.error,
                  }),
                },
                src: 'cancelEvent',
              },
            },
            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.cancellationFlow.identificationFlow:invocation[0]',
            input: ({context}) => {
              const eventDetails = context.eventDetails as IEvent;
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'cancel',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
      },
    },

    rescheduleFlow: {
      initial: 'init',
      states: {
        limitNotReached: {
          always: [
            {
              target: 'reasonForm',
              guard: {
                type: 'isHostOrInterviewer',
              },
            },
            {
              target: 'bookingFlow',
              guard: {
                type: 'isCandidate',
              },
              actions: assign({
                isReschedule: true,
              }),
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                  actions: assign({
                    reason: ({event}) => event.reason,
                  }),
                },
              },
            },

            submitting: {
              invoke: {
                id: 'rootNew.rescheduleFlow.rescheduleRequest.submitting:invocation[0]',
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  const eventDetails = context.eventDetails as IEvent;
                  if (eventDetails?.id && context.aid) {
                    return {
                      eventId: eventDetails.id,
                      reason: event.reason || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId or aid not found');
                },
                onDone: {
                  target: '#root.RequestRescheduleSuccess',
                  reenter: true,
                },
                onError: {
                  target: 'error',
                  actions: assign({error: ({event}) => event.error}),
                },
                src: 'sendRescheduleRequest',
              },
            },

            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        bookingFlow: {
          on: {
            'language.update': {
              actions: [
                assign({
                  language: ({event}) => event.language,
                }),
                sendTo(
                  ({system}) => system.get('bookingFlow'),
                  ({event}) => ({
                    type: 'language.update',
                    language: event.language,
                  }),
                ),
              ],
            },
          },
          invoke: {
            id: 'bookingFlow',
            input: ({context}) => {
              const {timezone, attendee, language} = context;
              if (timezone) {
                return {
                  timezone,
                  eventDetails: context.eventDetails as IEvent,
                  attendee,
                  language,
                };
              }
              throw new Error('timezone not found');
            },
            onDone: [
              {
                target: '#root.bookingSuccess',
                actions: assign({
                  eventDetails: ({event}) => event.output.eventDetails,
                  reason: ({event}) => event.output.rescheduleReason,
                }),
                guard: {
                  type: 'isBookingSuccess',
                },
              },
              {
                target: '#root.notifySuccess',
              },
            ],
            src: 'bookingMachine',
            systemId: 'bookingMachine',
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.rescheduleFlow.identificationFlow:invocation[0]',
            input: ({context}) => {
              const eventDetails = context.eventDetails as IEvent;
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'reschedule',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
        init: {
          always: [
            {
              target: 'rescheduleLimitReached',
              guard: 'isRescheduleLimitReached',
            },
            {
              target: 'limitNotReached',
              reenter: true,
            },
          ],
        },

        rescheduleLimitReached: {},
      },
    },

    rsvpFlow: {
      invoke: {
        id: 'rootNew.rsvpFlow:invocation[0]',
        input: ({context}) => {
          const {aid} = context;
          const eventDetails = context.eventDetails as IEvent;
          const rsvpStatus = getRsvpStatus(eventDetails, aid, context.owner);
          if (aid && eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              aid,
              rsvpStatus,
            };
          }
          throw new Error('aid and eventId not found');
        },
        src: 'rsvpMachine',
        systemId: 'rsvpMachine',
      },
    },

    bookingCancelForm: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
          actions: assign({
            reason: ({event}) => event.reason,
          }),
        },
      },
    },

    bookingCancelSubmitting: {
      invoke: {
        input: ({event, context}) => {
          const eventDetails = context.eventDetails as IEvent;
          assertEvent(event, 'bookingCancelForm.submit');
          if (eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              reason: event.reason,
              viewerTimezone: eventDetails?.primary_guest_timezone || '',
              attendee: context.attendee || '',
              aid: eventDetails?.candidates?.[0]?.id || context.aid || '',
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'cancelSuccess',
        },
        onError: {
          target: 'bookingCancelError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'cancelEvent',
      },
    },

    cancelSuccess: {
      type: 'final',
    },

    bookingCancelError: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
        },
      },
    },

    RequestRescheduleSuccess: {
      type: 'final',
    },

    attendeeGenerated: {
      on: {
        'reschedule.go': {
          target: 'rescheduleFlow',
        },
        'cancellation.go': {
          target: 'bookingCancelForm',
        },
      },
    },
  },
});

export type IRootMachineSnapshot = SnapshotFrom<typeof rootMachine>;
